import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Application,
  Assets,
  CanvasSource,
  Container,
  Graphics,
  Sprite,
  Texture,
} from 'pixi.js';
import { useLocation } from 'react-router';

import { CATEGORIES, practicePuzzle, todayPuzzle } from './puzzles';
import type { BoardCell, Puzzle } from './types';

import './WheelPrototype.css';

type LabWedgeKind = 'cash' | 'bankrupt' | 'lose' | 'free';

interface LabWedge {
  label: string;
  kind: LabWedgeKind;
  fill: string;
  ink: string;
  shape: 'circle' | 'triangle' | 'square' | 'diamond' | 'star' | 'dot';
}

const LAB_WEDGES: ReadonlyArray<LabWedge> = [
  { label: '$900', kind: 'cash', fill: '#F2C84B', ink: '#21140A', shape: 'dot' },
  { label: '$650', kind: 'cash', fill: '#4FB36E', ink: '#10170D', shape: 'triangle' },
  { label: '$1,200', kind: 'cash', fill: '#2389C7', ink: '#FDF4D9', shape: 'circle' },
  { label: '$500', kind: 'cash', fill: '#7865BF', ink: '#FFF2D0', shape: 'star' },
  { label: '$800', kind: 'cash', fill: '#F2932E', ink: '#21140A', shape: 'square' },
  { label: 'LOSE TURN', kind: 'lose', fill: '#596279', ink: '#FFF2D0', shape: 'diamond' },
  { label: '$700', kind: 'cash', fill: '#D46AA0', ink: '#FFF2D0', shape: 'dot' },
  { label: '$950', kind: 'cash', fill: '#97BE5D', ink: '#17200E', shape: 'triangle' },
  { label: '$550', kind: 'cash', fill: '#C94D43', ink: '#FFF2D0', shape: 'circle' },
  { label: '$2,500', kind: 'cash', fill: '#7456C3', ink: '#FFF2D0', shape: 'star' },
  { label: 'BANKRUPT', kind: 'bankrupt', fill: '#17100D', ink: '#FFF2D0', shape: 'diamond' },
  { label: '$5,000', kind: 'cash', fill: '#B3132F', ink: '#FFE18A', shape: 'star' },
  { label: 'BANKRUPT', kind: 'bankrupt', fill: '#17100D', ink: '#FFF2D0', shape: 'square' },
  { label: '$850', kind: 'cash', fill: '#E2BE46', ink: '#21140A', shape: 'diamond' },
  { label: '$600', kind: 'cash', fill: '#4FB36E', ink: '#10170D', shape: 'circle' },
  { label: '$750', kind: 'cash', fill: '#2389C7', ink: '#FDF4D9', shape: 'square' },
  { label: 'FREE PLAY', kind: 'free', fill: '#FFF0C4', ink: '#241508', shape: 'star' },
  { label: '$900', kind: 'cash', fill: '#D95543', ink: '#FFF2D0', shape: 'triangle' },
  { label: '$650', kind: 'cash', fill: '#A88D34', ink: '#21140A', shape: 'dot' },
  { label: '$1,500', kind: 'cash', fill: '#596279', ink: '#FFF2D0', shape: 'diamond' },
  { label: '$800', kind: 'cash', fill: '#F2932E', ink: '#21140A', shape: 'square' },
  { label: '$500', kind: 'cash', fill: '#D46AA0', ink: '#FFF2D0', shape: 'circle' },
  { label: '$700', kind: 'cash', fill: '#7865BF', ink: '#FFF2D0', shape: 'star' },
  { label: '$950', kind: 'cash', fill: '#97BE5D', ink: '#17200E', shape: 'triangle' },
];

const WEDGE_COUNT = LAB_WEDGES.length;
const WEDGE_RAD = (Math.PI * 2) / WEDGE_COUNT;
const WEDGE_DEG = 360 / WEDGE_COUNT;

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

function easeOutQuint(t: number): number {
  return 1 - Math.pow(1 - t, 5);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  const value = Number.parseInt(clean, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function mix(a: string, b: string, amount: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  const m = Math.max(0, Math.min(1, amount));
  const r = Math.round(ca.r + (cb.r - ca.r) * m);
  const g = Math.round(ca.g + (cb.g - ca.g) * m);
  const blue = Math.round(ca.b + (cb.b - ca.b) * m);
  return `rgb(${r}, ${g}, ${blue})`;
}

function drawWedgeText(
  ctx: CanvasRenderingContext2D,
  wedge: LabWedge,
  radius: number,
  midRad: number,
): void {
  ctx.save();
  ctx.rotate(midRad + Math.PI / 2);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';

  const isDarkInk =
    wedge.ink === '#21140A' ||
    wedge.ink === '#241508' ||
    wedge.ink === '#10170D' ||
    wedge.ink === '#17200E';
  const outline = isDarkInk ? 'rgba(255, 248, 218, 0.82)' : 'rgba(12, 5, 2, 0.78)';

  ctx.shadowColor = 'rgba(0, 0, 0, 0.24)';
  ctx.shadowBlur = radius * 0.0025;
  ctx.shadowOffsetY = radius * 0.0025;

  if (wedge.kind === 'cash') {
    const digits = wedge.label.replace(/\D/g, '').split('');
    const fontSize = radius * 0.074;
    const lineHeight = fontSize * 1.08;
    const centerY = -radius * 0.68;
    const yStart = centerY - ((digits.length - 1) * lineHeight) / 2;
    ctx.font = `950 ${fontSize}px Inter, Arial, sans-serif`;
    ctx.lineWidth = Math.max(4, fontSize * 0.13);
    ctx.strokeStyle = outline;
    ctx.fillStyle = wedge.ink;

    digits.forEach((digit, index) => {
      const y = yStart + index * lineHeight;
      ctx.strokeText(digit, 0, y);
      ctx.fillText(digit, 0, y);
    });

    ctx.font = `950 ${fontSize * 0.46}px Inter, Arial, sans-serif`;
    ctx.lineWidth = Math.max(3, fontSize * 0.1);
    ctx.strokeText('$', -fontSize * 0.44, yStart - lineHeight * 0.02);
    ctx.fillText('$', -fontSize * 0.44, yStart - lineHeight * 0.02);
    ctx.restore();
    return;
  }

  const word =
    wedge.kind === 'bankrupt'
      ? 'BANKRUPT'
      : wedge.kind === 'lose'
        ? 'LOSETURN'
        : 'FREE';
  const letters = word.split('');
  const fontSize = radius * (letters.length > 4 ? 0.034 : 0.052);
  const lineHeight = fontSize * 1.3;
  const centerY = -radius * 0.66;
  const yStart = centerY - ((letters.length - 1) * lineHeight) / 2;
  ctx.font = `950 ${fontSize}px Inter, Arial, sans-serif`;
  ctx.lineWidth = Math.max(3, fontSize * 0.11);
  ctx.strokeStyle = outline;
  ctx.fillStyle = wedge.ink;

  letters.forEach((letter, index) => {
    const y = yStart + index * lineHeight;
    ctx.strokeText(letter, 0, y);
    ctx.fillText(letter, 0, y);
  });
  ctx.restore();
}

function createWheelFace(size = 1800): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create wheel canvas context');

  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.47;
  const rimOuter = radius;
  const rimInner = radius * 0.9;
  const faceOuter = radius * 0.878;

  ctx.clearRect(0, 0, size, size);

  ctx.save();
  ctx.translate(cx, cy);

  for (let i = 0; i < WEDGE_COUNT; i += 1) {
    const wedge = LAB_WEDGES[i]!;
    const start = -Math.PI / 2 + (i - 0.5) * WEDGE_RAD;
    const end = start + WEDGE_RAD;
    const mid = (start + end) / 2;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, faceOuter, start, end);
    ctx.closePath();
    ctx.clip();

    const grad = ctx.createRadialGradient(
      Math.cos(mid) * radius * 0.18,
      Math.sin(mid) * radius * 0.18,
      radius * 0.06,
      Math.cos(mid) * radius * 0.73,
      Math.sin(mid) * radius * 0.73,
      radius * 0.58,
    );
    grad.addColorStop(0, mix(wedge.fill, '#FFFFFF', 0.48));
    grad.addColorStop(0.56, wedge.fill);
    grad.addColorStop(1, mix(wedge.fill, '#100806', 0.14));
    ctx.fillStyle = grad;
    ctx.fillRect(-radius, -radius, radius * 2, radius * 2);

    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.16)';
    ctx.beginPath();
    ctx.arc(0, 0, faceOuter, start, end);
    ctx.lineTo(0, 0);
    ctx.closePath();
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    ctx.restore();

    ctx.strokeStyle = 'rgba(255, 235, 171, 0.62)';
    ctx.lineWidth = radius * 0.005;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(start) * faceOuter, Math.sin(start) * faceOuter);
    ctx.stroke();

    drawWedgeText(ctx, wedge, radius, mid);
  }

  ctx.strokeStyle = 'rgba(255, 239, 196, 0.18)';
  ctx.lineWidth = radius * 0.004;
  for (const ring of [0.36, 0.58, 0.74]) {
    ctx.beginPath();
    ctx.arc(0, 0, radius * ring, 0, Math.PI * 2);
    ctx.stroke();
  }

  const rimGrad = ctx.createRadialGradient(
    -radius * 0.22,
    -radius * 0.28,
    radius * 0.12,
    0,
    0,
    rimOuter,
  );
  rimGrad.addColorStop(0, '#FFF0B2');
  rimGrad.addColorStop(0.26, '#F0C65A');
  rimGrad.addColorStop(0.64, '#B47C20');
  rimGrad.addColorStop(1, '#51330E');
  ctx.beginPath();
  ctx.arc(0, 0, rimOuter, 0, Math.PI * 2);
  ctx.arc(0, 0, rimInner, 0, Math.PI * 2, true);
  ctx.fillStyle = rimGrad;
  ctx.fill('evenodd');

  ctx.lineWidth = radius * 0.014;
  ctx.strokeStyle = 'rgba(60, 35, 8, 0.74)';
  ctx.beginPath();
  ctx.arc(0, 0, rimOuter - radius * 0.01, 0, Math.PI * 2);
  ctx.stroke();

  ctx.lineWidth = radius * 0.006;
  ctx.strokeStyle = 'rgba(255, 244, 196, 0.65)';
  ctx.beginPath();
  ctx.arc(0, 0, rimInner + radius * 0.012, 0, Math.PI * 2);
  ctx.stroke();

  const pegR = radius * 0.013;
  for (let i = 0; i < WEDGE_COUNT; i += 1) {
    const a = -Math.PI / 2 + (i - 0.5) * WEDGE_RAD;
    const x = Math.cos(a) * radius * 0.936;
    const y = Math.sin(a) * radius * 0.936;
    const pg = ctx.createRadialGradient(x - pegR * 0.35, y - pegR * 0.35, 1, x, y, pegR);
    pg.addColorStop(0, '#FFF7C6');
    pg.addColorStop(0.58, '#F0C65A');
    pg.addColorStop(1, '#5C390D');
    ctx.fillStyle = pg;
    ctx.beginPath();
    ctx.arc(x, y, pegR, 0, Math.PI * 2);
    ctx.fill();
  }

  const hubR = radius * 0.135;
  const hub = ctx.createRadialGradient(-hubR * 0.32, -hubR * 0.38, hubR * 0.08, 0, 0, hubR);
  hub.addColorStop(0, '#7B4922');
  hub.addColorStop(0.62, '#261106');
  hub.addColorStop(1, '#080302');
  ctx.fillStyle = hub;
  ctx.beginPath();
  ctx.arc(0, 0, hubR, 0, Math.PI * 2);
  ctx.fill();

  ctx.lineWidth = radius * 0.014;
  ctx.strokeStyle = '#F0C65A';
  ctx.beginPath();
  ctx.arc(0, 0, hubR * 0.92, 0, Math.PI * 2);
  ctx.stroke();

  const capR = hubR * 0.48;
  const cap = ctx.createRadialGradient(-capR * 0.38, -capR * 0.42, capR * 0.06, 0, 0, capR);
  cap.addColorStop(0, '#FFF6B8');
  cap.addColorStop(0.62, '#F3CB5E');
  cap.addColorStop(1, '#704711');
  ctx.fillStyle = cap;
  ctx.beginPath();
  ctx.arc(0, 0, capR, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();

  return Texture.from(canvas);
}

const VFX_TEXTURE_PATHS = {
  coin: '/generated/the-wheel/vfx-coin.png',
  sparkle: '/generated/the-wheel/vfx-sparkle.png',
  goldRibbon: '/generated/the-wheel/vfx-gold-ribbon.png',
  gem: '/generated/the-wheel/vfx-gem.png',
  flare: '/generated/the-wheel/vfx-flare.png',
  goldRing: '/generated/the-wheel/vfx-gold-ring.png',
  redImpact: '/generated/the-wheel/vfx-red-impact.png',
  smokeShards: '/generated/the-wheel/vfx-smoke-shards.png',
  grayRing: '/generated/the-wheel/vfx-gray-ring.png',
  emberComet: '/generated/the-wheel/vfx-ember-comet.png',
  silverRibbon: '/generated/the-wheel/vfx-silver-ribbon.png',
  jackpotBurst: '/generated/the-wheel/vfx-jackpot-burst.png',
} as const;

type VfxKey = keyof typeof VFX_TEXTURE_PATHS;
type VfxTextures = Record<VfxKey, Texture>;

async function loadVfxTextures(): Promise<VfxTextures> {
  const entries = await Promise.all(
    Object.entries(VFX_TEXTURE_PATHS).map(async ([key, path]) => {
      const texture = await Assets.load<Texture>(path).catch(() => makeFallbackVfxTexture());
      return [key, texture] as const;
    }),
  );
  return Object.fromEntries(entries) as VfxTextures;
}

function makeFallbackVfxTexture(): Texture {
  const size = 80;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create fallback VFX texture');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255, 255, 255, 1)');
  g.addColorStop(0.28, 'rgba(255, 225, 138, 0.86)');
  g.addColorStop(1, 'rgba(255, 225, 138, 0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.fill();
  return new Texture({ source: new CanvasSource({ resource: canvas }) });
}

function cashValue(label: string): number {
  return Number.parseInt(label.replace(/\D/g, ''), 10) || 0;
}

function pixiColor(hex: string): number {
  return Number.parseInt(hex.replace('#', ''), 16);
}

const VOWELS = new Set(['A', 'E', 'I', 'O', 'U']);
const VOWEL_COST = 250;
const CONSONANTS = 'BCDFGHJKLMNPQRSTVWXYZ'.split('');
const VOWEL_KEYS = 'AEIOU'.split('');
const MAX_BOARD_ROW = 14;

type LabPhase = 'ready' | 'spinning' | 'pick-consonant' | 'solving' | 'solved';
type LabMode = 'daily' | 'practice';

interface LabGameState {
  mode: LabMode;
  puzzle: Puzzle;
  revealedLetters: ReadonlySet<string>;
  usedLetters: ReadonlySet<string>;
  phase: LabPhase;
  roundScore: number;
  earnings: number;
  lastWedge: LabWedge | null;
  message: string;
  vowelHintUsed: boolean;
  solveError: string | null;
  puzzleNumber: number;
}

function modeFromSearch(search: string): LabMode {
  return new URLSearchParams(search).get('mode') === 'practice' ? 'practice' : 'daily';
}

function puzzleForMode(mode: LabMode): Puzzle {
  return mode === 'practice' ? practicePuzzle() : todayPuzzle();
}

function createInitialGameState(
  puzzle = todayPuzzle(),
  puzzleNumber = 1,
  mode: LabMode = 'daily',
): LabGameState {
  return {
    mode,
    puzzle,
    revealedLetters: new Set(),
    usedLetters: new Set(),
    phase: 'ready',
    roundScore: 0,
    earnings: 0,
    lastWedge: null,
    message: 'Spin the wheel to begin.',
    vowelHintUsed: false,
    solveError: null,
    puzzleNumber,
  };
}

function puzzleCells(puzzle: Puzzle, revealedLetters: ReadonlySet<string>): BoardCell[] {
  return puzzle.text.split('').map((raw) => {
    const letter = raw.toUpperCase();
    if (letter < 'A' || letter > 'Z') return { kind: 'space' };
    return revealedLetters.has(letter)
      ? { kind: 'revealed', letter }
      : { kind: 'hidden', letter };
  });
}

function packBoardRows(cells: ReadonlyArray<BoardCell>): BoardCell[][] {
  const words: BoardCell[][] = [];
  let word: BoardCell[] = [];
  for (const cell of cells) {
    if (cell.kind === 'space') {
      if (word.length > 0) {
        words.push(word);
        word = [];
      }
    } else {
      word.push(cell);
    }
  }
  if (word.length > 0) words.push(word);

  const rows: BoardCell[][] = [];
  let row: BoardCell[] = [];
  for (const nextWord of words) {
    if (nextWord.length > MAX_BOARD_ROW) {
      if (row.length > 0) {
        rows.push(row);
        row = [];
      }
      for (let i = 0; i < nextWord.length; i += MAX_BOARD_ROW) {
        rows.push(nextWord.slice(i, i + MAX_BOARD_ROW));
      }
      continue;
    }

    const nextLength = row.length === 0 ? nextWord.length : row.length + 1 + nextWord.length;
    if (nextLength > MAX_BOARD_ROW) {
      rows.push(row);
      row = [...nextWord];
    } else {
      if (row.length > 0) row.push({ kind: 'space' });
      row.push(...nextWord);
    }
  }
  if (row.length > 0) rows.push(row);
  return rows;
}

function countLetter(puzzle: Puzzle, letter: string): number {
  return puzzle.text.split('').filter((ch) => ch.toUpperCase() === letter).length;
}

function allPuzzleLetters(puzzle: Puzzle): Set<string> {
  const letters = new Set<string>();
  for (const raw of puzzle.text) {
    const letter = raw.toUpperCase();
    if (letter >= 'A' && letter <= 'Z') letters.add(letter);
  }
  return letters;
}

function isPuzzleSolved(puzzle: Puzzle, revealedLetters: ReadonlySet<string>): boolean {
  return puzzle.text.split('').every((raw) => {
    const letter = raw.toUpperCase();
    return letter < 'A' || letter > 'Z' || revealedLetters.has(letter);
  });
}

function normalizeSolve(value: string): string {
  return value.toUpperCase().replace(/[^A-Z ]/g, '').replace(/\s+/g, ' ').trim();
}

function deductVowelCost(roundScore: number, earnings: number): { roundScore: number; earnings: number } {
  if (roundScore >= VOWEL_COST) return { roundScore: roundScore - VOWEL_COST, earnings };
  return { roundScore: 0, earnings: Math.max(0, earnings - (VOWEL_COST - roundScore)) };
}

function canCallConsonantFor(wedge: LabWedge | null): wedge is LabWedge & { kind: 'cash' | 'free' } {
  return wedge?.kind === 'cash' || wedge?.kind === 'free';
}

function landingProfile(wedge: LabWedge): {
  color: number;
  duration: number;
  strength: number;
} {
  if (wedge.kind === 'bankrupt') {
    return { color: 0xd94a34, duration: 0.78, strength: 0.72 };
  }
  if (wedge.kind === 'lose') {
    return { color: 0xb5c1d4, duration: 0.62, strength: 0.52 };
  }
  if (wedge.kind === 'free') {
    return { color: 0xfff2b0, duration: 0.95, strength: 0.78 };
  }
  const value = cashValue(wedge.label);
  return {
    color: value >= 2500 ? 0xffe26d : pixiColor(wedge.fill),
    duration: value >= 2500 ? 1.2 : 0.78,
    strength: Math.min(1, 0.42 + value / 6400),
  };
}

interface LabSpin {
  start: number;
  end: number;
  startedAt: number;
  durationMs: number;
  targetIndex: number;
  lastWedge: number;
}

interface Spark {
  sprite: Sprite;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  gravity: number;
  spin: number;
  startScale: number;
  endScale: number;
  alpha: number;
  drag: number;
  fadeIn: number;
}

interface LandingFx {
  wedge: LabWedge;
  age: number;
  duration: number;
  color: number;
  strength: number;
}

interface QueuedBurst {
  wedge: LabWedge;
  delay: number;
  x: number;
  y: number;
  scale: number;
}

interface VfxEmitOptions {
  texture: Texture;
  x: number;
  y: number;
  angle: number;
  speed: number;
  gravity: number;
  life: number;
  startScale: number;
  endScale?: number;
  spin?: number;
  tint?: number;
  alpha?: number;
  drag?: number;
  fadeIn?: number;
  blend?: 'normal' | 'add';
}

class WheelLabScene {
  private app = new Application();
  private world = new Container();
  private wheel = new Container();
  private wheelSprite: Sprite | null = null;
  private highlight = new Graphics();
  private impact = new Graphics();
  private rimChase = new Graphics();
  private tickFlash = new Graphics();
  private pointer = new Graphics();
  private vfxTextures: VfxTextures | null = null;
  private sparks: Spark[] = [];
  private container: HTMLElement | null = null;
  private ro: ResizeObserver | null = null;
  private rotation = 0;
  private spin: LabSpin | null = null;
  private selectedIndex: number | null = null;
  private selectedPulse = 0;
  private pointerBend = 0;
  private pointerSnap = 0;
  private spinVelocity = 0;
  private landingFx: LandingFx | null = null;
  private queuedBursts: QueuedBurst[] = [];
  private wheelKick = 0;
  private wheelKickStrength = 0;
  private size = 0;
  private destroyed = false;
  private pointerIndex = 0;
  private onPointer: (wedge: LabWedge) => void;
  private onLand: (wedge: LabWedge) => void;
  private onSpinStart: () => void;

  constructor(opts: {
    onPointer: (wedge: LabWedge) => void;
    onLand: (wedge: LabWedge) => void;
    onSpinStart: () => void;
  }) {
    this.onPointer = opts.onPointer;
    this.onLand = opts.onLand;
    this.onSpinStart = opts.onSpinStart;
  }

  async init(container: HTMLElement): Promise<void> {
    this.container = container;
    await this.app.init({
      resizeTo: container,
      backgroundAlpha: 0,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio, 2),
      autoDensity: true,
    });
    if (this.destroyed) {
      this.app.destroy(true, { children: true });
      return;
    }

    container.appendChild(this.app.canvas);
    this.app.canvas.style.width = '100%';
    this.app.canvas.style.height = '100%';
    this.app.canvas.style.display = 'block';

    this.wheelSprite = new Sprite(createWheelFace());
    this.wheelSprite.anchor.set(0.5);
    this.wheel.addChild(this.wheelSprite);
    this.wheel.addChild(this.highlight);
    this.world.addChild(this.wheel);
    this.world.addChild(this.impact);
    this.world.addChild(this.rimChase);
    this.world.addChild(this.tickFlash);

    this.vfxTextures = await loadVfxTextures();
    if (this.destroyed) return;
    for (let i = 0; i < 260; i += 1) {
      const sprite = new Sprite(this.vfxTextures.sparkle);
      sprite.anchor.set(0.5);
      sprite.visible = false;
      this.world.addChild(sprite);
      this.sparks.push({
        sprite,
        vx: 0,
        vy: 0,
        life: 0,
        maxLife: 1,
        gravity: 0,
        spin: 0,
        startScale: 1,
        endScale: 1,
        alpha: 1,
        drag: 0,
        fadeIn: 0,
      });
    }

    this.world.addChild(this.pointer);
    this.app.stage.addChild(this.world);
    this.layout();
    this.onPointer(LAB_WEDGES[this.pointerIndex]!);
    this.ro = new ResizeObserver(() => this.layout());
    this.ro.observe(container);
    this.app.ticker.add(this.tick);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    const appWithTicker = this.app as Application & {
      ticker?: { remove: (fn: () => void) => void };
    };
    appWithTicker.ticker?.remove(this.tick);
    this.ro?.disconnect();
    this.ro = null;
    this.vfxTextures = null;
    this.wheelSprite?.texture.destroy(true);
    this.wheelSprite = null;
    try {
      this.app.destroy(true, { children: true });
    } catch {
      // Pixi may still be inside async init during React StrictMode cleanup.
    }
  }

  spinToRandom(): boolean {
    if (this.spin) return false;
    const targetIndex = Math.floor(Math.random() * WEDGE_COUNT);
    const landingOffset = (Math.random() - 0.5) * 0.58;
    const current = mod(this.rotation, Math.PI * 2);
    const target = mod(-(targetIndex + landingOffset) * WEDGE_RAD, Math.PI * 2);
    const delta = mod(target - current, Math.PI * 2);
    const rotations = 4 + Math.floor(Math.random() * 3);
    this.spin = {
      start: this.rotation,
      end: this.rotation + rotations * Math.PI * 2 + delta,
      startedAt: performance.now(),
      durationMs: 3900 + Math.random() * 900,
      targetIndex,
      lastWedge: this.wedgeAt(this.rotation),
    };
    this.selectedIndex = null;
    this.landingFx = null;
    this.queuedBursts = [];
    this.wheelKick = 0;
    this.wheelKickStrength = 0;
    this.pointerSnap = 0;
    this.wheel.position.set(0, 0);
    this.wheel.scale.set(1);
    this.impact.clear();
    this.rimChase.clear();
    this.tickFlash.clear();
    this.highlight.clear();
    this.onSpinStart();
    return true;
  }

  private layout(): void {
    if (!this.container || !this.wheelSprite) return;
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    // Leave room for pointer overhang and celebration sprites inside the
    // canvas. A full 96% wheel looked large, but clipped at the viewport edge
    // on shorter/wider layouts.
    this.size = Math.min(w * 0.88, h * 0.8);
    this.world.position.set(w / 2, h / 2 + this.size * 0.035);
    const scale = this.size / this.wheelSprite.texture.width;
    this.wheelSprite.scale.set(scale);
    this.drawPointer();
  }

  private wedgeAt(rotation: number): number {
    const deg = mod((-rotation * 180) / Math.PI, 360);
    return Math.floor((deg + WEDGE_DEG / 2) / WEDGE_DEG) % WEDGE_COUNT;
  }

  private drawPointer(): void {
    const top = this.pointerOriginY();
    const g = this.pointer;
    g.clear();
    g.position.set(0, top);
    g.rotation = this.pointerBend * 0.26;
    const s = this.size / 680;
    g.poly([
      -26 * s, -52 * s,
      26 * s, -52 * s,
      19 * s, -8 * s,
      6 * s, 56 * s,
      0, 72 * s,
      -6 * s, 56 * s,
      -19 * s, -8 * s,
    ]);
    g.fill(0xffd96f);
    g.stroke({ color: 0x6a3c0b, width: 3 * s, alpha: 0.96 });
    g.poly([
      -14 * s, -36 * s,
      14 * s, -36 * s,
      10 * s, -4 * s,
      0, 50 * s,
      -10 * s, -4 * s,
    ]);
    g.fill(0x321407);
    g.circle(0, -60 * s, 22 * s);
    g.fill(0xffd96f);
    g.stroke({ color: 0x6a3c0b, width: 3 * s });
    g.circle(0, -60 * s, 11 * s);
    g.fill(0x321407);
  }

  private pointerScale(): number {
    return this.size / 680;
  }

  private pointerLocalTipY(): number {
    return 72 * this.pointerScale();
  }

  private pointerContactY(): number {
    return -this.size * 0.424;
  }

  private pointerOriginY(): number {
    return this.pointerContactY() - this.pointerLocalTipY();
  }

  private pointerLoadForRotation(rotation: number): number {
    const phase = mod(-rotation / WEDGE_RAD + 0.5, 1);
    if (phase > 0.72) return 0;
    const load = 1 - phase / 0.72;
    const eased = load * load * (3 - 2 * load);
    return -0.74 * eased;
  }

  private updatePointerMotion(dt: number): void {
    const loading = this.spin ? this.pointerLoadForRotation(this.rotation) : 0;
    this.pointerSnap *= Math.max(0, 1 - dt * 11);
    if (Math.abs(this.pointerSnap) < 0.012) this.pointerSnap = 0;

    const nextBend = loading + this.pointerSnap;
    if (Math.abs(nextBend - this.pointerBend) > 0.001 || Math.abs(nextBend) > 0) {
      this.pointerBend = nextBend;
      this.drawPointer();
    }
  }

  private drawHighlight(index: number): void {
    const r = this.size / 2;
    const inner = r * 0.14;
    const outer = r * 0.823;
    const start = -Math.PI / 2 + (index - 0.5) * WEDGE_RAD;
    const end = start + WEDGE_RAD;
    const wedge = LAB_WEDGES[index]!;
    const profile = landingProfile(wedge);
    const pulse = 0.55 + Math.sin(this.selectedPulse) * 0.12;
    this.highlight.clear();
    this.highlight.moveTo(Math.cos(start) * inner, Math.sin(start) * inner);
    this.highlight.arc(0, 0, outer, start, end);
    this.highlight.lineTo(Math.cos(end) * inner, Math.sin(end) * inner);
    this.highlight.arc(0, 0, inner, end, start, true);
    this.highlight.closePath();
    this.highlight.fill({ color: profile.color, alpha: 0.035 + pulse * 0.045 * profile.strength });
    this.highlight.stroke({
      color: profile.color,
      width: Math.max(4, r * (0.008 + profile.strength * 0.004)),
      alpha: wedge.kind === 'bankrupt' ? 0.62 : 0.86,
    });
  }

  private emitPointerTick(wedge: LabWedge): void {
    const textures = this.vfxTextures;
    if (!textures) return;

    const color =
      wedge.kind === 'bankrupt'
        ? 0xff6b4a
        : wedge.kind === 'lose'
          ? 0xd4d9e4
          : 0xffe18a;
    const wheelDirection = this.spin && this.spin.end >= this.spin.start ? 1 : -1;
    const speed01 = Math.min(1, this.spinVelocity / 28);
    const count = Math.round(7 - speed01 * 3);
    const baseLife = 0.038 + (1 - speed01) * 0.05;
    const baseScale = this.size / 18000;
    const contactX = 0;
    const contactY = this.pointerContactY() + this.size * 0.002;
    const tangentAngle = wheelDirection > 0 ? 0 : Math.PI;
    const texturePool =
      wedge.kind === 'bankrupt'
        ? [textures.emberComet, textures.redImpact, textures.sparkle]
        : wedge.kind === 'lose'
          ? [textures.silverRibbon, textures.smokeShards, textures.sparkle]
          : [textures.sparkle, textures.flare, textures.goldRibbon];

    for (let i = 0; i < count; i += 1) {
      const texture = texturePool[Math.floor(Math.random() * texturePool.length)]!;
      const angle = tangentAngle - Math.PI * (0.16 + Math.random() * 0.14) + (Math.random() - 0.5) * 0.58;
      const life = baseLife * (0.72 + Math.random() * 0.52);
      const startScale = baseScale * (0.42 + Math.random() * 0.62) * (texture === textures.flare ? 0.76 : 1);
      const offset = (Math.random() - 0.5) * this.size * 0.007;
      const lift = Math.random() * this.size * 0.005;

      this.emitVfxSprite({
        texture,
        x: contactX + offset,
        y: contactY - lift,
        angle,
        speed: this.size * (0.11 + Math.random() * 0.18 + speed01 * 0.08),
        gravity: this.size * 0.08,
        life,
        startScale,
        endScale: startScale * (0.24 + Math.random() * 0.22),
        spin: (Math.random() - 0.5) * 18,
        alpha: 0.76 + Math.random() * 0.2,
        drag: 7.5,
        fadeIn: 0.012,
        tint: i === 0 ? 0xffffff : color,
        blend: 'add',
      });
    }
  }

  private drawImpact(): void {
    this.impact.clear();
    if (!this.landingFx) return;

    const fx = this.landingFx;
    const p = Math.min(1, fx.age / fx.duration);
    const out = Math.pow(1 - p, 2);
    const r = this.size / 2;
    const topY = -r + this.size * 0.13;

    if (fx.wedge.kind === 'bankrupt' || fx.wedge.kind === 'lose') {
      const alpha = out * fx.strength;
      this.impact.circle(0, 0, r * (0.75 + p * 0.12));
      this.impact.stroke({
        color: fx.color,
        width: Math.max(8, r * 0.026 * out),
        alpha: alpha * 0.74,
      });
      this.impact.circle(0, topY + r * 0.05, r * (0.09 + p * 0.1));
      this.impact.fill({ color: fx.color, alpha: alpha * 0.34 });
      this.impact.moveTo(-r * 0.13, topY - r * 0.03);
      this.impact.lineTo(r * 0.13, topY + r * 0.06);
      this.impact.moveTo(r * 0.13, topY - r * 0.03);
      this.impact.lineTo(-r * 0.13, topY + r * 0.06);
      this.impact.stroke({
        color: fx.color,
        width: Math.max(4, r * 0.012 * out),
        alpha: alpha * 0.7,
      });
      return;
    }

    const alpha = out * fx.strength;
    this.impact.circle(0, 0, r * (0.28 + p * 0.75));
    this.impact.stroke({
      color: fx.color,
      width: Math.max(6, r * 0.018 * out),
      alpha: alpha * 0.58,
    });
    this.impact.circle(0, 0, r * (0.54 + p * 0.34));
    this.impact.stroke({
      color: 0xfff2b0,
      width: Math.max(3, r * 0.008 * out),
      alpha: alpha * 0.38,
    });
    this.impact.circle(0, topY, r * (0.055 + p * 0.13));
    this.impact.fill({ color: fx.color, alpha: alpha * 0.2 });
  }

  private drawRimChase(): void {
    this.rimChase.clear();
    if (!this.landingFx) return;

    const fx = this.landingFx;
    const p = Math.min(1, fx.age / fx.duration);
    const out = Math.pow(1 - p, 1.35);
    const r = this.size / 2;
    const rimR = r * 0.918;

    if (fx.wedge.kind === 'bankrupt' || fx.wedge.kind === 'lose') {
      this.rimChase.arc(0, 0, rimR, -Math.PI * 0.92, Math.PI * 0.92);
      this.rimChase.stroke({
        color: fx.color,
        width: Math.max(5, r * 0.014 * out),
        alpha: out * fx.strength * 0.56,
      });
      return;
    }

    const isBigCash = fx.wedge.kind === 'cash' && cashValue(fx.wedge.label) >= 2500;
    const chaseCount = isBigCash ? 5 : fx.wedge.kind === 'free' ? 4 : 2;
    const sweep = (isBigCash ? 0.54 : 0.34) + p * 0.38;

    for (let i = 0; i < chaseCount; i += 1) {
      const start = -Math.PI * 0.92 + p * Math.PI * 2.15 + i * ((Math.PI * 2) / chaseCount);
      const end = start + sweep;
      this.rimChase.arc(0, 0, rimR - i * r * 0.012, start, end);
      this.rimChase.stroke({
        color: i % 2 === 0 ? fx.color : 0xfff2b0,
        width: Math.max(3, r * (isBigCash ? 0.012 : 0.008) * out),
        alpha: out * fx.strength * (isBigCash ? 0.8 : 0.5),
      });
    }
  }

  private emitVfxSprite(opts: VfxEmitOptions): boolean {
    const spark = this.sparks.find((candidate) => candidate.life <= 0);
    if (!spark) return false;

    spark.sprite.texture = opts.texture;
    spark.sprite.position.set(opts.x, opts.y);
    spark.sprite.rotation = Math.random() * Math.PI * 2;
    spark.sprite.scale.set(opts.startScale);
    spark.sprite.tint = opts.tint ?? 0xffffff;
    spark.sprite.alpha = 0;
    spark.sprite.visible = true;
    spark.sprite.blendMode = opts.blend ?? 'normal';
    spark.vx = Math.cos(opts.angle) * opts.speed;
    spark.vy = Math.sin(opts.angle) * opts.speed;
    spark.life = opts.life;
    spark.maxLife = opts.life;
    spark.gravity = opts.gravity;
    spark.spin = opts.spin ?? 0;
    spark.startScale = opts.startScale;
    spark.endScale = opts.endScale ?? opts.startScale;
    spark.alpha = opts.alpha ?? 1;
    spark.drag = opts.drag ?? 0;
    spark.fadeIn = opts.fadeIn ?? 0.04;
    return true;
  }

  private emitParticleBurst(
    wedge: LabWedge,
    x: number,
    y: number,
    scale = 1,
  ): void {
    if (!this.vfxTextures) return;

    const textures = this.vfxTextures;
    const value = cashValue(wedge.label);
    const isBigCash = wedge.kind === 'cash' && value >= 2500;

    const unit = this.size / 760;
    const burstScale = unit * scale;
    const emitArc = (
      count: number,
      texturePool: Texture[],
      opts: {
        spread: number;
        speed: [number, number];
        gravity: number;
        life: [number, number];
        size: [number, number];
        yBias?: number;
        spin?: number;
        tint?: number[];
        blend?: 'normal' | 'add';
      },
    ): void => {
      for (let i = 0; i < count; i += 1) {
        const texture = texturePool[Math.floor(Math.random() * texturePool.length)]!;
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * opts.spread;
        const speed = this.size * burstScale * (opts.speed[0] + Math.random() * (opts.speed[1] - opts.speed[0]));
        const life = opts.life[0] + Math.random() * (opts.life[1] - opts.life[0]);
        const startScale = burstScale * (opts.size[0] + Math.random() * (opts.size[1] - opts.size[0]));
        const tint = opts.tint?.[Math.floor(Math.random() * opts.tint.length)];
        this.emitVfxSprite({
          texture,
          x: x + (Math.random() - 0.5) * this.size * 0.035 * scale,
          y: y + (opts.yBias ?? 0) + (Math.random() - 0.5) * this.size * 0.025 * scale,
          angle,
          speed,
          gravity: this.size * opts.gravity,
          life,
          startScale,
          endScale: startScale * (texture === textures.flare || texture === textures.goldRing ? 1.8 : 0.72),
          spin: (Math.random() - 0.5) * (opts.spin ?? 5),
          alpha: opts.blend === 'add' ? 0.92 : 1,
          drag: 0.1,
          ...(tint === undefined ? {} : { tint }),
          ...(opts.blend === undefined ? {} : { blend: opts.blend }),
        });
      }
    };

    if (wedge.kind === 'bankrupt') {
      this.emitVfxSprite({
        texture: textures.redImpact,
        x,
        y: y + this.size * 0.018,
        angle: Math.PI / 2,
        speed: 0,
        gravity: 0,
        life: 0.54,
        startScale: burstScale * 0.76,
        endScale: burstScale * 1.18,
        alpha: 0.95,
        blend: 'add',
      });
      this.emitVfxSprite({
        texture: textures.smokeShards,
        x,
        y: y + this.size * 0.04,
        angle: Math.PI / 2,
        speed: this.size * burstScale * 0.05,
        gravity: this.size * 0.22,
        life: 0.92,
        startScale: burstScale * 0.64,
        endScale: burstScale * 1.08,
        alpha: 0.82,
      });
      emitArc(Math.round(18 * scale), [textures.emberComet], {
        spread: 1.7,
        speed: [0.14, 0.32],
        gravity: 0.64,
        life: [0.5, 0.82],
        size: [0.13, 0.28],
        yBias: this.size * 0.025,
        spin: 2,
        tint: [0xff4b2f, 0x9b241b, 0x5f1a13],
        blend: 'add',
      });
      return;
    }

    if (wedge.kind === 'lose') {
      this.emitVfxSprite({
        texture: textures.grayRing,
        x,
        y,
        angle: Math.PI / 2,
        speed: 0,
        gravity: 0,
        life: 0.56,
        startScale: burstScale * 0.44,
        endScale: burstScale * 0.95,
        alpha: 0.76,
      });
      emitArc(Math.round(12 * scale), [textures.smokeShards, textures.silverRibbon], {
        spread: 1.2,
        speed: [0.08, 0.2],
        gravity: 0.5,
        life: [0.54, 0.8],
        size: [0.09, 0.2],
        yBias: this.size * 0.02,
        spin: 4,
        tint: [0xb8bdc8, 0x8f97a8, 0xffffff],
      });
      return;
    }

    const mainRingScale = isBigCash ? 0.95 : wedge.kind === 'free' ? 0.74 : 0.54;
    this.emitVfxSprite({
      texture: isBigCash ? textures.jackpotBurst : textures.flare,
      x,
      y,
      angle: -Math.PI / 2,
      speed: 0,
      gravity: 0,
      life: isBigCash ? 0.96 : 0.62,
      startScale: burstScale * mainRingScale,
      endScale: burstScale * mainRingScale * (isBigCash ? 1.55 : 1.42),
      alpha: isBigCash ? 0.9 : 0.72,
      blend: 'add',
    });
    this.emitVfxSprite({
      texture: textures.goldRing,
      x,
      y,
      angle: -Math.PI / 2,
      speed: 0,
      gravity: 0,
      life: isBigCash ? 1.05 : 0.72,
      startScale: burstScale * (isBigCash ? 0.62 : 0.42),
      endScale: burstScale * (isBigCash ? 1.28 : 0.86),
      alpha: 0.68,
      blend: 'add',
    });

    const coinCount = isBigCash ? 58 : wedge.kind === 'free' ? 22 : Math.min(36, Math.round(10 + value / 100));
    const ribbonCount = isBigCash ? 34 : wedge.kind === 'free' ? 16 : Math.min(18, Math.round(5 + value / 220));
    const sparkleCount = isBigCash ? 44 : wedge.kind === 'free' ? 20 : Math.min(24, Math.round(8 + value / 180));
    emitArc(coinCount, [textures.coin, textures.gem], {
      spread: isBigCash ? 2.65 : 1.72,
      speed: isBigCash ? [0.28, 0.58] : [0.2, 0.38],
      gravity: 0.42,
      life: isBigCash ? [1.05, 1.64] : [0.78, 1.18],
      size: isBigCash ? [0.12, 0.24] : [0.08, 0.16],
      spin: isBigCash ? 9 : 7,
      tint: [0xffffff, 0xfff2b0, 0xffd45d],
    });
    emitArc(ribbonCount, [textures.goldRibbon, textures.silverRibbon], {
      spread: isBigCash ? 2.35 : 1.55,
      speed: isBigCash ? [0.22, 0.48] : [0.16, 0.3],
      gravity: 0.32,
      life: isBigCash ? [1.15, 1.8] : [0.9, 1.32],
      size: isBigCash ? [0.12, 0.23] : [0.08, 0.15],
      spin: 8,
      tint: [0xffffff, 0xffe18a],
    });
    emitArc(sparkleCount, [textures.sparkle, textures.flare], {
      spread: isBigCash ? 2.95 : 1.9,
      speed: isBigCash ? [0.2, 0.52] : [0.14, 0.32],
      gravity: 0.18,
      life: isBigCash ? [0.7, 1.18] : [0.46, 0.82],
      size: isBigCash ? [0.08, 0.18] : [0.05, 0.11],
      spin: 3,
      tint: [0xffffff, 0xfff2b0, 0xffe26d],
      blend: 'add',
    });

    if (isBigCash) {
      for (let i = 0; i < 4; i += 1) {
        const side = i % 2 === 0 ? -1 : 1;
        this.emitVfxSprite({
          texture: textures.jackpotBurst,
          x: x + side * this.size * (0.12 + i * 0.025),
          y: y + this.size * (-0.08 + i * 0.035),
          angle: -Math.PI / 2,
          speed: 0,
          gravity: 0,
          life: 0.74 + i * 0.08,
          startScale: burstScale * (0.34 + i * 0.04),
          endScale: burstScale * (0.62 + i * 0.08),
          alpha: 0.72,
          blend: 'add',
          fadeIn: 0.08 + i * 0.025,
        });
      }
    }
  }

  private burstAtPointer(wedge: LabWedge): void {
    const y = -this.size / 2 + this.size * 0.11;
    this.emitParticleBurst(wedge, 0, y, 1);
    this.queueFollowUpBursts(wedge);
  }

  private queueFollowUpBursts(wedge: LabWedge): void {
    this.queuedBursts = [];
    if (wedge.kind === 'bankrupt' || wedge.kind === 'lose') return;

    const value = cashValue(wedge.label);
    const isBigCash = wedge.kind === 'cash' && value >= 2500;
    const r = this.size / 2;
    const burstCount = wedge.kind === 'free' ? 4 : isBigCash ? 10 : value >= 1000 ? 4 : 2;
    const arcStart = -Math.PI * 0.92;
    const arcEnd = -Math.PI * 0.08;

    for (let i = 0; i < burstCount; i += 1) {
      const t = i / Math.max(1, burstCount - 1);
      const a = arcStart + (arcEnd - arcStart) * t + (Math.random() - 0.5) * 0.12;
      this.queuedBursts.push({
        wedge,
        delay: 0.08 + i * (isBigCash ? 0.065 : 0.1),
        x: Math.cos(a) * r * (0.62 + Math.random() * 0.13),
        y: Math.sin(a) * r * (0.68 + Math.random() * 0.08),
        scale: isBigCash ? 0.92 : 0.56,
      });
    }
  }

  private tick = (): void => {
    const dt = Math.min(this.app.ticker.deltaMS / 1000, 0.05);
    if (this.spin) {
      const elapsed = performance.now() - this.spin.startedAt;
      const t = Math.min(1, elapsed / this.spin.durationMs);
      const previousRotation = this.rotation;
      this.rotation = this.spin.start + (this.spin.end - this.spin.start) * easeOutQuint(t);
      this.spinVelocity = dt > 0 ? Math.abs(this.rotation - previousRotation) / dt : 0;
      this.wheel.rotation = this.rotation;
      const currentWedge = this.wedgeAt(this.rotation);
      if (currentWedge !== this.spin.lastWedge) {
        this.spin.lastWedge = currentWedge;
        this.pointerIndex = currentWedge;
        const wedge = LAB_WEDGES[currentWedge]!;
        this.onPointer(wedge);
        this.pointerBend = -0.78;
        this.emitPointerTick(wedge);
        this.pointerSnap = 0.62;
      }
      if (t >= 1) {
        this.rotation = this.spin.end;
        this.wheel.rotation = this.rotation;
        this.selectedIndex = this.spin.targetIndex;
        this.landingFx = {
          wedge: LAB_WEDGES[this.spin.targetIndex]!,
          age: 0,
          ...landingProfile(LAB_WEDGES[this.spin.targetIndex]!),
        };
        this.wheelKick = 1;
        this.wheelKickStrength = this.landingFx.strength;
        this.pointerIndex = this.spin.targetIndex;
        this.selectedPulse = 0;
        this.burstAtPointer(LAB_WEDGES[this.spin.targetIndex]!);
        this.onPointer(LAB_WEDGES[this.spin.targetIndex]!);
        this.onLand(LAB_WEDGES[this.spin.targetIndex]!);
        this.spin = null;
      }
    } else {
      this.spinVelocity = 0;
    }

    this.updatePointerMotion(dt);

    if (this.selectedIndex !== null) {
      this.selectedPulse += dt * 5;
      this.drawHighlight(this.selectedIndex);
    }

    if (this.wheelKick > 0) {
      this.wheelKick = Math.max(0, this.wheelKick - dt * 2.8);
      const wobble = Math.sin((1 - this.wheelKick) * Math.PI * 11) * this.wheelKick;
      const bump = 1 + this.wheelKick * this.wheelKickStrength * 0.016;
      this.wheel.scale.set(bump);
      this.wheel.position.set(wobble * this.size * 0.006, Math.abs(wobble) * this.size * 0.003);
      if (this.wheelKick === 0) {
        this.wheel.scale.set(1);
        this.wheel.position.set(0, 0);
      }
    }

    if (this.landingFx) {
      this.landingFx.age += dt;
      this.drawImpact();
      this.drawRimChase();
      if (this.landingFx.age >= this.landingFx.duration) {
        this.landingFx = null;
        this.impact.clear();
        this.rimChase.clear();
      }
    }

    if (this.queuedBursts.length > 0) {
      for (let i = this.queuedBursts.length - 1; i >= 0; i -= 1) {
        const burst = this.queuedBursts[i]!;
        burst.delay -= dt;
        if (burst.delay > 0) continue;
        this.emitParticleBurst(burst.wedge, burst.x, burst.y, burst.scale);
        this.queuedBursts.splice(i, 1);
      }
    }

    for (const spark of this.sparks) {
      if (spark.life <= 0) continue;
      spark.life -= dt;
      spark.sprite.x += spark.vx * dt;
      spark.sprite.y += spark.vy * dt;
      spark.vy += spark.gravity * dt;
      const drag = Math.max(0, 1 - spark.drag * dt);
      spark.vx *= drag;
      spark.vy *= drag;
      spark.sprite.rotation += spark.spin * dt;
      const remaining = Math.max(0, spark.life / spark.maxLife);
      const age = 1 - remaining;
      const scale = spark.startScale + (spark.endScale - spark.startScale) * age;
      spark.sprite.scale.set(scale);
      const fadeIn = spark.fadeIn > 0 ? Math.min(1, age / spark.fadeIn) : 1;
      spark.sprite.alpha = spark.alpha * fadeIn * Math.pow(remaining, 0.82);
      if (spark.life <= 0) {
        spark.sprite.visible = false;
        spark.sprite.blendMode = 'normal';
      }
    }
  };
}

export function WheelPrototype() {
  const location = useLocation();
  const mountRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<WheelLabScene | null>(null);
  const solveInputRef = useRef<HTMLInputElement | null>(null);
  const [pointerWedge, setPointerWedge] = useState<LabWedge>(LAB_WEDGES[0]!);
  const [landing, setLanding] = useState<LabWedge | null>(null);
  const [landingEventId, setLandingEventId] = useState(0);
  const [game, setGame] = useState<LabGameState>(() => {
    const mode = modeFromSearch(location.search);
    return createInitialGameState(puzzleForMode(mode), 1, mode);
  });
  const [solveGuess, setSolveGuess] = useState('');

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const scene = new WheelLabScene({
      onPointer: (wedge) => setPointerWedge(wedge),
      onSpinStart: () => {
        setLanding(null);
        setGame((current) => ({
          ...current,
          phase: 'spinning',
          lastWedge: null,
          solveError: null,
          message: 'The wheel is spinning.',
        }));
      },
      onLand: (wedge) => {
        setLanding(wedge);
        setLandingEventId((eventId) => eventId + 1);
        setGame((current) => {
          if (wedge.kind === 'bankrupt') {
            return {
              ...current,
              phase: 'ready',
              lastWedge: wedge,
              roundScore: 0,
              message: 'Bankrupt. Round score reset; earnings are safe.',
            };
          }
          if (wedge.kind === 'lose') {
            return {
              ...current,
              phase: 'ready',
              lastWedge: wedge,
              message: 'Lose a turn. Spin again when you are ready.',
            };
          }
          if (wedge.kind === 'free') {
            return {
              ...current,
              phase: 'pick-consonant',
              lastWedge: wedge,
              message: 'Free Play. Call a consonant.',
            };
          }
          return {
            ...current,
            phase: 'pick-consonant',
            lastWedge: wedge,
            message: `Call a consonant for ${wedge.label}.`,
          };
        });
      },
    });
    sceneRef.current = scene;
    void scene.init(mount);
    return () => {
      scene.destroy();
      sceneRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (game.phase === 'solving') {
      solveInputRef.current?.focus();
    }
  }, [game.phase]);

  const cells = useMemo(
    () => puzzleCells(game.puzzle, game.revealedLetters),
    [game.puzzle, game.revealedLetters],
  );
  const rows = useMemo(() => packBoardRows(cells), [cells]);
  const categoryMeta = CATEGORIES.find((category) => category.id === game.puzzle.category)!;
  const canSpin = game.phase === 'ready';
  const canSolve = game.phase === 'ready' || game.phase === 'pick-consonant';
  const canHint = game.phase === 'ready' && !game.vowelHintUsed;
  const canBuyVowel = game.phase === 'ready' && game.roundScore + game.earnings >= VOWEL_COST;
  const canPickConsonant = game.phase === 'pick-consonant' && canCallConsonantFor(game.lastWedge);

  const finishIfSolved = (next: LabGameState, revealedLetters: ReadonlySet<string>): LabGameState => {
    if (!isPuzzleSolved(next.puzzle, revealedLetters)) return next;
    return {
      ...next,
      phase: 'solved',
      revealedLetters: allPuzzleLetters(next.puzzle),
      lastWedge: null,
      earnings: next.earnings + next.roundScore,
      roundScore: 0,
      message: `Solved. Earnings are now $${(next.earnings + next.roundScore).toLocaleString()}.`,
    };
  };

  const handleSpin = () => {
    if (!canSpin) return;
    sceneRef.current?.spinToRandom();
  };

  const handleConsonant = (letter: string) => {
    setGame((current) => {
      if (current.phase !== 'pick-consonant' || !canCallConsonantFor(current.lastWedge)) return current;
      if (VOWELS.has(letter) || current.usedLetters.has(letter)) return current;

      const usedLetters = new Set(current.usedLetters);
      usedLetters.add(letter);
      const matches = countLetter(current.puzzle, letter);
      if (matches === 0) {
        return {
          ...current,
          phase: 'ready',
          usedLetters,
          lastWedge: null,
          message: `No ${letter}. Spin again.`,
        };
      }

      const revealedLetters = new Set(current.revealedLetters);
      revealedLetters.add(letter);
      const value = current.lastWedge.kind === 'cash' ? cashValue(current.lastWedge.label) : 0;
      const added = value * matches;
      const next = {
        ...current,
        phase: 'ready' as const,
        usedLetters,
        revealedLetters,
        lastWedge: null,
        roundScore: current.roundScore + added,
        message:
          matches === 1
            ? `${letter} appears once. ${added > 0 ? `+$${added.toLocaleString()}.` : 'Free reveal.'}`
            : `${letter} appears ${matches} times. ${added > 0 ? `+$${added.toLocaleString()}.` : 'Free reveal.'}`,
      };
      return finishIfSolved(next, revealedLetters);
    });
  };

  const handleVowel = (letter: string) => {
    setGame((current) => {
      if (current.phase !== 'ready' || !VOWELS.has(letter) || current.usedLetters.has(letter)) return current;
      if (current.roundScore + current.earnings < VOWEL_COST) return current;

      const usedLetters = new Set(current.usedLetters);
      usedLetters.add(letter);
      const balance = deductVowelCost(current.roundScore, current.earnings);
      const matches = countLetter(current.puzzle, letter);
      const revealedLetters = new Set(current.revealedLetters);
      if (matches > 0) revealedLetters.add(letter);
      const next = {
        ...current,
        ...balance,
        usedLetters,
        revealedLetters,
        message:
          matches === 0
            ? `No ${letter}.`
            : matches === 1
              ? `${letter} appears once.`
              : `${letter} appears ${matches} times.`,
      };
      return finishIfSolved(next, revealedLetters);
    });
  };

  const handleLetter = (letter: string) => {
    if (VOWELS.has(letter)) handleVowel(letter);
    else handleConsonant(letter);
  };

  const handleHint = () => {
    setGame((current) => {
      if (current.phase !== 'ready' || current.vowelHintUsed) return current;
      const revealedLetters = new Set(current.revealedLetters);
      const usedLetters = new Set(current.usedLetters);
      for (const letter of VOWEL_KEYS) {
        if (usedLetters.has(letter) || countLetter(current.puzzle, letter) === 0) continue;
        revealedLetters.add(letter);
        usedLetters.add(letter);
        const next = {
          ...current,
          revealedLetters,
          usedLetters,
          vowelHintUsed: true,
          message: `Here is a vowel: ${letter}.`,
        };
        return finishIfSolved(next, revealedLetters);
      }
      return {
        ...current,
        vowelHintUsed: true,
        message: 'No hidden vowels remain.',
      };
    });
  };

  const beginSolve = () => {
    if (!canSolve) return;
    setSolveGuess('');
    setGame((current) => ({ ...current, phase: 'solving', solveError: null, message: 'Solve the puzzle.' }));
  };

  const cancelSolve = () => {
    setGame((current) => ({
      ...current,
      phase: canCallConsonantFor(current.lastWedge) ? 'pick-consonant' : 'ready',
      solveError: null,
    }));
  };

  const submitSolve = () => {
    setGame((current) => {
      if (current.phase !== 'solving') return current;
      if (normalizeSolve(solveGuess) !== normalizeSolve(current.puzzle.text)) {
        return {
          ...current,
          phase: canCallConsonantFor(current.lastWedge) ? 'pick-consonant' : 'ready',
          solveError: 'That is not it. Take another look.',
          message: 'That is not it. Take another look.',
        };
      }
      return {
        ...current,
        phase: 'solved',
        revealedLetters: allPuzzleLetters(current.puzzle),
        usedLetters: allPuzzleLetters(current.puzzle),
        lastWedge: null,
        earnings: current.earnings + current.roundScore,
        roundScore: 0,
        solveError: null,
        message: `Solved. Earnings are now $${(current.earnings + current.roundScore).toLocaleString()}.`,
      };
    });
  };

  const nextPuzzle = () => {
    setLanding(null);
    setSolveGuess('');
    setGame((current) => ({
      ...createInitialGameState(practicePuzzle(), current.puzzleNumber + 1, 'practice'),
      earnings: current.earnings,
      message: 'New puzzle. Spin the wheel.',
    }));
  };

  const outcomeTone =
    landing?.kind === 'cash'
      ? cashValue(landing.label) >= 2500
        ? 'big-cash'
        : 'cash'
      : landing?.kind ?? 'ready';

  return (
    <div className="wheel-prototype" data-outcome-tone={outcomeTone}>
      {landing ? (
        <div
          className="wheel-prototype__outcome-flash"
          data-outcome-tone={outcomeTone}
          key={landingEventId}
        />
      ) : null}
      <header className="wheel-prototype__header">
        <div>
          <p className="wheel-prototype__eyebrow">
            {game.mode === 'daily' && game.puzzleNumber === 1 ? "Today's Puzzle" : 'Practice Puzzle'}
          </p>
          <h1 className="wheel-prototype__title">The Wheel</h1>
        </div>
        <div className="wheel-prototype__category" aria-label={`Category: ${categoryMeta.label}`}>
          <span>Category</span>
          <strong>{categoryMeta.label}</strong>
          <em>{categoryMeta.hint}</em>
        </div>
      </header>

      <PuzzleBoard rows={rows} />

      <main className="wheel-prototype__play" aria-label="Wheel game">
        <section className="wheel-prototype__stage" aria-label="Spinning wheel">
          <div className="wheel-prototype__reader" data-kind={pointerWedge.kind} aria-live="polite">
            <span>Current Wedge</span>
            <strong>{pointerWedge.label}</strong>
          </div>
          <div className="wheel-prototype__canvas" ref={mountRef} />
        </section>

        <aside className="wheel-prototype__console" aria-label="Game controls">
          <div className="wheel-prototype__message" aria-live="polite">
            {game.message}
          </div>
          {game.solveError ? <div className="wheel-prototype__error">{game.solveError}</div> : null}
          <div
            className="wheel-prototype__result"
            data-kind={landing?.kind ?? 'ready'}
            key={landingEventId}
          >
            <span>Landed On</span>
            <strong>{landing?.label ?? 'Ready'}</strong>
          </div>
          <div className="wheel-prototype__score" aria-label="Score">
            <div>
              <span>Round</span>
              <strong>${game.roundScore.toLocaleString()}</strong>
            </div>
            <div>
              <span>Earnings</span>
              <strong>${game.earnings.toLocaleString()}</strong>
            </div>
          </div>
          <div className="wheel-prototype__actions">
            <button
              className="wheel-prototype__spin"
              type="button"
              onClick={handleSpin}
              disabled={!canSpin}
            >
              Spin
            </button>
            <button
              className="wheel-prototype__button wheel-prototype__button--solve"
              type="button"
              onClick={beginSolve}
              disabled={!canSolve}
            >
              Solve
            </button>
            <button
              className="wheel-prototype__button wheel-prototype__button--hint"
              type="button"
              onClick={handleHint}
              disabled={!canHint}
            >
              Show me a vowel
            </button>
          </div>
        </aside>
      </main>

      <LetterKeyboard
        usedLetters={game.usedLetters}
        canPickConsonant={canPickConsonant}
        canBuyVowel={canBuyVowel}
        onLetter={handleLetter}
      />

      {game.phase === 'solving' ? (
        <div className="wheel-prototype__solve-overlay" role="dialog" aria-modal="true" aria-labelledby="wheel-solve-title">
          <form
            className="wheel-prototype__solve"
            onSubmit={(event) => {
              event.preventDefault();
              submitSolve();
            }}
          >
            <h2 id="wheel-solve-title">Solve the puzzle</h2>
            <input
              ref={solveInputRef}
              value={solveGuess}
              onChange={(event) => setSolveGuess(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') cancelSolve();
              }}
              aria-label="Puzzle solution"
              maxLength={game.puzzle.text.length + 8}
            />
            <div className="wheel-prototype__solve-actions">
              <button className="wheel-prototype__button wheel-prototype__button--solve" type="submit">
                Submit
              </button>
              <button className="wheel-prototype__button" type="button" onClick={cancelSolve}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {game.phase === 'solved' ? (
        <div className="wheel-prototype__complete" role="region" aria-label="Puzzle solved">
          <span>Solved</span>
          <strong>{game.puzzle.text}</strong>
          <button className="wheel-prototype__button wheel-prototype__button--solve" type="button" onClick={nextPuzzle}>
            New puzzle
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PuzzleBoard({ rows }: { rows: BoardCell[][] }) {
  return (
    <section className="wheel-prototype__board" aria-label="Puzzle board">
      {rows.map((row, rowIndex) => (
        <div className="wheel-prototype__board-row" key={rowIndex}>
          {row.map((cell, cellIndex) => (
            <PuzzleTile cell={cell} key={`${rowIndex}-${cellIndex}`} />
          ))}
        </div>
      ))}
    </section>
  );
}

function PuzzleTile({ cell }: { cell: BoardCell }) {
  if (cell.kind === 'space') {
    return <span className="wheel-prototype__tile wheel-prototype__tile--space" aria-hidden="true" />;
  }

  const revealed = cell.kind === 'revealed';
  return (
    <span
      className={`wheel-prototype__tile ${revealed ? 'is-revealed' : ''}`}
      aria-label={revealed ? cell.letter : 'hidden letter'}
    >
      {revealed ? cell.letter : ''}
    </span>
  );
}

function LetterKeyboard({
  usedLetters,
  canPickConsonant,
  canBuyVowel,
  onLetter,
}: {
  usedLetters: ReadonlySet<string>;
  canPickConsonant: boolean;
  canBuyVowel: boolean;
  onLetter: (letter: string) => void;
}) {
  return (
    <section className="wheel-prototype__keyboard" aria-label="Letter keyboard">
      <div className="wheel-prototype__key-row wheel-prototype__key-row--vowels">
        <span>Vowels - ${VOWEL_COST}</span>
        {VOWEL_KEYS.map((letter) => (
          <button
            key={letter}
            className="wheel-prototype__key wheel-prototype__key--vowel"
            type="button"
            disabled={!canBuyVowel || usedLetters.has(letter)}
            aria-pressed={usedLetters.has(letter)}
            onClick={() => onLetter(letter)}
          >
            {letter}
          </button>
        ))}
      </div>
      <div className="wheel-prototype__key-row">
        <span>Consonants</span>
        {CONSONANTS.map((letter) => (
          <button
            key={letter}
            className="wheel-prototype__key"
            type="button"
            disabled={!canPickConsonant || usedLetters.has(letter)}
            aria-pressed={usedLetters.has(letter)}
            onClick={() => onLetter(letter)}
          >
            {letter}
          </button>
        ))}
      </div>
    </section>
  );
}
